import { GetStaticPaths, GetStaticProps } from 'next'
import React, { ReactElement } from 'react'

import { dehydrate, QueryClient } from 'react-query'

import { getAllUsersSlugs } from 'app/services/Api/user'
import { listingKeys } from 'app/services/Query/listing/listing.keys'
import { listingConfig } from 'app/services/Query/listing/listing.query'
import { userKeys } from 'app/services/Query/user/user.keys'
import { userConfig } from 'app/services/Query/user/user.query'
import { NextPage } from 'app/types/decalarations/next'
import { REVIEWS_VARIANT } from 'app/utils/constants/status.constants'

import StoreLayout from '~/components/layouts/StoreLayout'
import ReviewsView from '~/views/Store/ReviewsView'

const StoreReviewsPage: NextPage = () => <ReviewsView />

StoreReviewsPage.getLayout = (page: ReactElement) => (
  <StoreLayout>{page}</StoreLayout>
)

export const getStaticPaths: GetStaticPaths = async () => {
  const { slugs } = await getAllUsersSlugs()

  const paths = slugs.map((slug) => ({ params: { slug } }))

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async (context) => {
  const revalidate = 60
  const slug = context.params?.slug as string
  const receive = context.params?.receive as string

  try {
    const queryClient = new QueryClient()

    const productsKeyQuery = listingKeys.getListingsByUser({ slug, page: 1 })

    await queryClient.prefetchQuery(
      productsKeyQuery,
      listingConfig.getListingsByUser.request
    )

    const listingsState =
      queryClient.getQueryState<PaginationListingModel>(productsKeyQuery)

    const hasListings =
      !!listingsState?.data?.listings?.length &&
      listingsState?.data?.listings?.length > 0

    if (hasListings) {
      const userKeyQuery = userKeys.getUserBySlug(slug)

      await queryClient.prefetchQuery(
        userKeyQuery,
        userConfig.getUserBySlug.request
      )

      const state = queryClient.getQueryState(userKeyQuery)

      if (state?.status === 'error') throw new Error(String(state?.error))

      const user = queryClient.getQueryData(userKeyQuery) as UserBySlugModel

      const isReceivedSeller =
        user?.is_seller_reviews || user?.available_listings
      const startReceive = REVIEWS_VARIANT[receive] || REVIEWS_VARIANT.seller
      const receiveDefault = isReceivedSeller
        ? startReceive
        : REVIEWS_VARIANT.buyer

      const reviewsKeyQuery = userKeys.getUserReviews({
        [receiveDefault]: user?.id,
        page: 1
      })

      await queryClient.prefetchQuery(
        reviewsKeyQuery,
        userConfig.getUserReviews.request
      )
    }

    return {
      revalidate,
      props: {
        dehydratedState: dehydrate(queryClient)
      }
    }
  } catch (error) {
    return { notFound: true, revalidate }
  }
}

export default StoreReviewsPage
